"use client";

import * as React from "react";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import type { BlockBase } from "@/cms/blocks/registry/types";
import {
  getLocaleUI,
  pickFirstNonEmpty,
} from "../utils";
import type { ContentOption, ContentReferenceLike } from "../types";

const CONTENT_TYPE_LABELS: Record<string, string> = {
  ARTICLE: "Article",
  STATIC_PAGE: "Static Page",
  PRODUCT_LIST: "Product List",
};

const CONTENT_STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  review: "Review",
  published: "Published",
  archived: "Archived",
};

function getContentDisplayTitle(
  data: ContentReferenceLike,
  locale: "en" | "fa",
): string {
  const primary =
    locale === "fa"
      ? pickFirstNonEmpty(data.title_fa, data.title_en)
      : pickFirstNonEmpty(data.title_en, data.title_fa);
  if (primary) return primary;
  const slug =
    locale === "fa"
      ? pickFirstNonEmpty(data.slug_fa, data.slug_en)
      : pickFirstNonEmpty(data.slug_en, data.slug_fa);
  if (slug) return slug;
  return data.contentId ?? "";
}

function getContentDisplaySlug(
  data: ContentReferenceLike,
  locale: "en" | "fa",
): string {
  const slug =
    locale === "fa"
      ? pickFirstNonEmpty(data.slug_fa, data.slug_en)
      : pickFirstNonEmpty(data.slug_en, data.slug_fa);
  return slug ?? "";
}

function formatContentTypeLabel(type: string | undefined): string {
  if (!type) return "";
  return CONTENT_TYPE_LABELS[type] ?? type.replace(/_/g, " ");
}

function formatContentStatusLabel(status: string | undefined): string {
  if (!status) return "";
  return CONTENT_STATUS_LABELS[status] ?? status;
}

export function ContentReferenceEditor({
  block,
  onChange,
  locale,
}: {
  block: BlockBase;
  onChange: (patch: (data: any) => any) => void;
  locale: "en" | "fa";
}) {
  const data = (block.data as any) || {};
  const [query, setQuery] = React.useState<string>(
    getContentDisplayTitle(data, locale),
  );
  const debouncedQuery = useDebouncedValue(query, 300);
  const [options, setOptions] = React.useState<ContentOption[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const { dir: inputDir, inputClass: localizedInputClass } =
    getLocaleUI(locale);

  const contentSnapshot = React.useMemo<ContentReferenceLike>(
    () => ({
      contentId: data.contentId,
      type: data.type,
      status: data.status,
      title_en: data.title_en,
      title_fa: data.title_fa,
      slug_en: data.slug_en,
      slug_fa: data.slug_fa,
    }),
    [
      data.contentId,
      data.type,
      data.status,
      data.title_en,
      data.title_fa,
      data.slug_en,
      data.slug_fa,
    ],
  );

  React.useEffect(() => {
    setQuery(getContentDisplayTitle(contentSnapshot, locale));
  }, [contentSnapshot, locale]);

  React.useEffect(() => {
    let cancelled = false;
    const trimmed = debouncedQuery.trim();
    if (trimmed.length < 2) {
      setOptions([]);
      setOpen(false);
      return;
    }
    const fetchOptions = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        params.set("q", trimmed);
        params.set("take", "10");
        const res = await fetch(`/api/cms/content?${params.toString()}`);
        const json = await res.json();
        if (cancelled) return;
        const items = Array.isArray(json?.items) ? (json.items as any[]) : [];
        setOptions(
          items
            .filter((item) => item && typeof item.id === "string")
            .map(
              (item) =>
                ({
                  id: item.id,
                  type: item.type,
                  status: item.status,
                  title_en: item.title_en ?? null,
                  title_fa: item.title_fa ?? null,
                  slug_en: item.slug_en ?? null,
                  slug_fa: item.slug_fa ?? null,
                }) as ContentOption,
            ),
        );
        setOpen(items.length > 0);
      } catch (error) {
        console.warn("content search failed", error);
        if (!cancelled) {
          setOptions([]);
          setOpen(false);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    void fetchOptions();
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery]);

  const handleSelect = (option: ContentOption) => {
    onChange((prev) => ({
      ...prev,
      contentId: option.id,
      type: option.type,
      status: option.status,
      title_en: option.title_en ?? "",
      title_fa: option.title_fa ?? "",
      slug_en: option.slug_en ?? "",
      slug_fa: option.slug_fa ?? "",
    }));
    setQuery(getContentDisplayTitle(option, locale));
    setOptions([]);
    setOpen(false);
  };

  const clearSelection = () => {
    onChange((prev) => ({
      ...prev,
      contentId: "",
      type: "",
      status: "",
      title_en: "",
      title_fa: "",
      slug_en: "",
      slug_fa: "",
    }));
    setQuery("");
    setOptions([]);
    setOpen(false);
  };

  const selectedTitle = getContentDisplayTitle(data, locale);
  const selectedSlug = getContentDisplaySlug(data, locale);
  const typeLabel = formatContentTypeLabel(data.type);
  const statusLabel = formatContentStatusLabel(data.status);
  const normalizedQuery = query.trim();
  const normalizedSelected = selectedTitle.trim();
  const showNoResults =
    !loading &&
    normalizedQuery.length >= 2 &&
    options.length === 0 &&
    normalizedQuery !== normalizedSelected;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm opacity-70">Select content</span>
        {data.contentId && (
          <button
            type="button"
            className="text-red-600"
            onClick={clearSelection}
          >
            Change
          </button>
        )}
      </div>
      <div className="relative">
        <input
          dir={inputDir}
          className={`w-full ${localizedInputClass}`}
          placeholder="Search by title or slug…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onFocus={() => options.length && setOpen(true)}
        />
        {open && options.length > 0 && (
          <div className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded border bg-white shadow-lg">
            {options.map((option) => {
              const displayTitle = getContentDisplayTitle(option, locale);
              const displaySlug = getContentDisplaySlug(option, locale);
              const optionType = formatContentTypeLabel(option.type);
              const optionStatus = formatContentStatusLabel(option.status);
              return (
                <button
                  type="button"
                  key={option.id}
                  className="w-full px-3 py-2 text-left hover:bg-gray-100"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => handleSelect(option)}
                >
                  <div className="truncate font-medium">
                    {displayTitle || option.id}
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                    <span>{optionType}</span>
                    <span>• {optionStatus}</span>
                    {displaySlug ? (
                      <span className="truncate">Slug: {displaySlug}</span>
                    ) : null}
                  </div>
                </button>
              );
            })}
          </div>
        )}
        {showNoResults && (
          <div className="absolute z-10 mt-1 w-full rounded border bg-white p-2 text-sm text-gray-500 shadow">
            No content found.
          </div>
        )}
        {loading && (
          <div className="absolute top-1/2 right-3 -translate-y-1/2 text-xs text-gray-500">
            Loading…
          </div>
        )}
      </div>
      {data.contentId ? (
        <div className="rounded border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600">
          <div className="text-xs uppercase opacity-60">
            {typeLabel}
            {statusLabel ? ` · ${statusLabel}` : ""}
          </div>
          <div className="truncate font-semibold">
            {selectedTitle || data.contentId}
          </div>
          {selectedSlug && (
            <div className="truncate text-xs text-gray-500">
              Slug: {selectedSlug}
            </div>
          )}
        </div>
      ) : (
        <div className="text-sm text-gray-500">No content selected.</div>
      )}
    </div>
  );
}
